import { useFormik } from 'formik'
import i18n from 'i18next'
import { isEqual } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import Select from 'react-select'
import { Card, CardBody, CardHeader, Col, Form, Input, Label, Row, TabContent, TabPane, UncontrolledTooltip } from 'reactstrap'
import * as Yup from 'yup'
import agency from '../../../../api/agency/hooks'
import agents from '../../../../api/agents/hooks'
import auth from '../../../../api/auth/hooks'
import flags from '../../../../api/flags/hooks'
import settings from '../../../../api/settings/hooks'
import { ENTITY_TYPE } from '../../../../common/constants'
import BreadCrumb from '../../../../Components/Common/BreadCrumb'
import FormActionButtons from '../../../../Components/Common/FormActionButtons'
import FormTabNavigation from '../../../../Components/Common/FormTabNavigation'
import InputText from '../../../../Components/FormUI/InputText'
import PhoneWithDropDown from '../../../../Components/FormUI/PhoneWithDropDown'
import { useInitializeLocationFilters } from '../../../../hooks/useInitializeLocationFilters'
import { useLocationValues } from '../../../../hooks/useLocationValues'
import { resetFormFilters, setFormFilter } from '../../../../store/formFilters'
import AdminLoader from '../../Components/AdminLoader/AdminLoader'
import AgentPermissions from '../../Components/AgentPermissions/AgentPermissions'
import NotificationPreferences from '../../Components/NotificationPreferences/NotificationPreferences'
import UploadPhoto from '../../Components/UploadPhoto/UploadPhoto'
import { createSelectOptionData, formatPhoneNumber } from '../../helpers'
import { userFieldDescription } from '../userFieldDescription'
import { userValidation } from '../validateRules'

const UserEdit = () => {
    const { id } = useParams()
    const dispatch = useDispatch()
    const history = useHistory()

    const { data: featureFlags } = flags.GetFeatureFlags.useQuery()
    const { data: applicationSettings } = settings.GetApplicationSettings.useQuery({
        select: (data) => ({
            applicationPackage: data.applicationPackage,
            availableLanguages: data.availableLanguages,
        }),
    })
    const { data: currentAgentId } = auth.CurrentAgent.useQuery({ select: (data) => data.id })
    const { data: user = {}, isLoading: fetchingUser } = agents.GetAgent.useQuery(id)
    const { data: agencyOffices = {} } = agency.GetAgencyOffices.useQuery()
    const { data: agentPermissions } = agents.GetAgentPermissions.useQuery()
    const { mutate: updateAgent } = agents.UpdateAgent.useMutation()

    const applicationPackage = applicationSettings?.applicationPackage
    const availableLanguages = applicationSettings?.availableLanguages

    const tabs = [
        { id: 1, name: 'agentInfo', label: i18n.t('admin.agentInfo') },
        { id: 2, name: 'permissions', label: i18n.t('admin.permissions') },
        ...(featureFlags?.notifications_system ? [{ id: 3, name: 'notifications', label: i18n.t('app.common.notifications') }] : []),
    ]

    const [activeTab, setActiveTab] = useState(tabs[0].name)
    const description = userFieldDescription()

    const tabChange = (tab) => {
        if (activeTab !== tab) setActiveTab(tab)
    }

    const userLocationData = useMemo(() => {
        if (!user || user.id !== +id) return null

        return {
            country: user.defaultCountry,
            county: user.defaultCounty,
            city: user.defaultCity,
        }
    }, [user, id])

    useInitializeLocationFilters(userLocationData)
    const { locationValues } = useLocationValues(ENTITY_TYPE.ADMINISTRATION)

    useEffect(() => {
        return () => {
            dispatch(resetFormFilters({ context: 'location' }))
        }
    }, [dispatch])

    // some permissions form fields might be added or removed from agentPermissions on the backend
    // if so, this will update user.agentPermissions with new/missing permissions from /agent-permissions endpoint
    const updatedAgentPermissions = useMemo(() => {
        if (!agentPermissions?.length || !user.agentPermissions?.length) {
            return []
        }

        const userPermissionMap = new Map(user.agentPermissions.map((item) => [item.permission?.id, item.permissionLevel]))

        return agentPermissions.map((permission) => {
            const permissionLevel = userPermissionMap.get(permission.id)

            return {
                permission: { ...permission },
                permissionLevel: { ...permissionLevel },
            }
        })
    }, [user.agentPermissions, agentPermissions])

    const formattedUser = useMemo(() => {
        return {
            ...user,
            agentPermissions: updatedAgentPermissions,
        }
    }, [user, updatedAgentPermissions])

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: !fetchingUser && formattedUser,
        validationSchema: Yup.object(userValidation()),
        onSubmit: (values) => {
            const agentPermissionsArray = values.agentPermissions.map((item) => {
                return {
                    agentPermission_id: item.permission.id,
                    agentPermissionLevel_id: item?.permissionLevel?.id,
                }
            })

            const { id: agentId, agency: _agency, agencyOffice, defaultCounty, defaultCountry, defaultCity, applicationLanguage, ...data } = values

            updateAgent({
                agentId,
                data: {
                    ...data,
                    agency_office_id: agencyOffice?.id ?? null,
                    defaultCounty_id: defaultCounty?.id ?? null,
                    defaultCountry_id: defaultCountry?.id ?? null,
                    defaultCity_id: defaultCity?.id ?? null,
                    applicationLanguage_id: applicationLanguage?.id ?? null,
                    agentPermissions: agentPermissionsArray,
                    mobileNumber: formatPhoneNumber(values?.mobileNumber),
                    phoneNumber: formatPhoneNumber(values?.phoneNumber),
                    permissionsChanged: handlePermissionsChange(agentPermissionsArray),
                },
            })
        },
    })

    const handleDropDownChange = (e, propertyName) => {
        const property = e ? { id: parseInt(e.value), name: e.label } : null

        const locationFieldsMap = {
            defaultCountry: 'country',
            defaultCounty: 'county',
            defaultCity: 'city',
        }

        formik?.setFieldValue(propertyName, property)

        if (locationFieldsMap[propertyName]) {
            dispatch(setFormFilter({ context: 'location', field: locationFieldsMap[propertyName], value: e?.value || null }))

            if (propertyName === 'defaultCountry') {
                formik?.setFieldValue('defaultCounty', null)
                formik?.setFieldValue('defaultCity', null)
            } else if (propertyName === 'defaultCounty') {
                formik?.setFieldValue('defaultCity', null)
            }
        }
    }

    const handlePermissionsChange = (newPermissions) => {
        const currentPermissions = user.agentPermissions.map((item) => ({
            agentPermission_id: item?.permission.id,
            agentPermissionLevel_id: item?.permissionLevel.id,
        }))

        return !isEqual(newPermissions, currentPermissions)
    }

    return fetchingUser ? (
        <AdminLoader />
    ) : (
        <Row>
            <BreadCrumb title={`${i18n.t('page.edit')} ${formik?.values?.firstName} ${formik?.values?.lastName}`} />
            <UploadPhoto
                photoField={'photo'}
                formik={formik}
                imageUrl={formik?.values?.photo}
                firstName={formik?.values?.firstName}
                lastName={formik?.values?.lastName}
                headline={`${formik?.values?.firstName} ${formik?.values?.lastName}`}
                subheadline={`${formik?.values?.companyRole || ''}`}
                permission={description.photo}
            />
            <Col xxl={9}>
                <Card>
                    <CardHeader>
                        <FormTabNavigation
                            tabs={tabs}
                            activeTab={activeTab}
                            handleTabChange={tabChange}
                            formik={formik}
                            customClass="nav-tabs-custom rounded card-header-tabs border-bottom-0"
                        />
                    </CardHeader>
                    <CardBody className="p-4">
                        <Form onSubmit={formik.handleSubmit}>
                            <TabContent activeTab={activeTab}>
                                <TabPane tabId="agentInfo">
                                    <Row>
                                        <Col lg={6}>
                                            <div className="mb-3">
                                                <InputText
                                                    label={i18n.t('app.common.firstName')}
                                                    id={'firstName'}
                                                    formik={formik}
                                                    isRequired={true}
                                                    permission={description.firstName}
                                                />
                                            </div>
                                        </Col>
                                        <Col lg={6}>
                                            <div className="mb-3">
                                                <InputText
                                                    label={i18n.t('app.common.lastName')}
                                                    id={'lastName'}
                                                    formik={formik}
                                                    isRequired={true}
                                                    permission={description.lastName}
                                                />
                                            </div>
                                        </Col>
                                        <Col lg={6}>
                                            <div className="mb-3">
                                                <Label htmlFor="agencyOffice" className="form-label">
                                                    {i18n.t('admin.officeLabel')}
                                                </Label>
                                                {description.agencyOffice && (
                                                    <span className="ps-1">
                                                        <span id={`agentPermissionsTooltipagencyOffice`}>
                                                            <i className="mdi mdi-help-circle-outline"></i>{' '}
                                                        </span>
                                                        <UncontrolledTooltip placement="right" target={`agentPermissionsTooltipagencyOffice`}>
                                                            {description.agencyOffice.description}
                                                        </UncontrolledTooltip>
                                                    </span>
                                                )}
                                                <Select
                                                    inputId="agencyOffice"
                                                    id={formik.values?.agencyOffice?.id}
                                                    onChange={(e) => {
                                                        handleDropDownChange(e, 'agencyOffice')
                                                    }}
                                                    options={createSelectOptionData(agencyOffices?.items, 'name', 'id')}
                                                    placeholder={i18n.t('admin.selectAgencyOffice')}
                                                    value={
                                                        createSelectOptionData(agencyOffices?.items, 'name', 'id')?.filter(
                                                            (office) => office?.value === formik.values?.agencyOffice?.id
                                                        )[0]
                                                    }
                                                />
                                            </div>
                                        </Col>
                                        <Col lg={6}>
                                            <div className="mb-3">
                                                <InputText
                                                    label={i18n.t('app.common.jobTitle')}
                                                    id={'companyRole'}
                                                    formik={formik}
                                                    permission={description.companyRole}
                                                />
                                            </div>
                                        </Col>
                                        <Col lg={6}>
                                            <div className="mb-3">
                                                <PhoneWithDropDown
                                                    label={i18n.t('admin.phone')}
                                                    id={'phoneNumber'}
                                                    formik={formik}
                                                    defaultValue={formik.values?.phoneNumber}
                                                    permission={description.phone}
                                                />
                                            </div>
                                        </Col>
                                        <Col lg={6}>
                                            <div className="mb-3">
                                                <PhoneWithDropDown
                                                    label={i18n.t('admin.mobilePhone')}
                                                    id={'mobileNumber'}
                                                    formik={formik}
                                                    defaultValue={formik.values?.mobileNumber}
                                                    permission={description.mobileNumber}
                                                />
                                            </div>
                                        </Col>
                                        <Col lg={6}>
                                            <div className="mb-3">
                                                <InputText
                                                    label={i18n.t('app.common.email.label')}
                                                    id={'primaryEmail'}
                                                    formik={formik}
                                                    isDisabled={true}
                                                    permission={description.primaryEmail}
                                                />
                                            </div>
                                        </Col>
                                        <Col lg={6} className="d-flex align-items-center mt-3">
                                            <div className="d-flex align-items-center">
                                                <Input
                                                    type="checkbox"
                                                    checked={formik.values?.ccEmail || false}
                                                    id={'ccEmail'}
                                                    onChange={formik.handleChange}
                                                    className="form-control me-2 p-2"
                                                />
                                                <Label htmlFor="ccEmail" className="form-label m-0">
                                                    {i18n.t('admin.ccEmail')}
                                                </Label>
                                                {description.ccEmail && (
                                                    <span className="ps-1">
                                                        <span id={`agentPermissionsTooltipccEmail`}>
                                                            <i className="mdi mdi-help-circle-outline"></i>{' '}
                                                        </span>
                                                        <UncontrolledTooltip placement="right" target={`agentPermissionsTooltipccEmail`}>
                                                            {description.ccEmail.description}
                                                        </UncontrolledTooltip>
                                                    </span>
                                                )}
                                            </div>
                                        </Col>
                                        <Col lg={6}>
                                            <div className="mb-3">
                                                <InputText
                                                    label={i18n.t('admin.displayEmail')}
                                                    id={'displayEmail'}
                                                    formik={formik}
                                                    permission={description.displayEmail}
                                                />
                                            </div>
                                        </Col>
                                        <Col lg={6} className="d-flex align-items-center mt-4">
                                            <div className="mb-3 d-flex align-items-center">
                                                <Input
                                                    id={'active'}
                                                    type="checkbox"
                                                    checked={formik.values?.active || false}
                                                    onChange={formik.handleChange}
                                                    className="form-control me-2 p-2"
                                                    disabled={currentAgentId === user?.id}
                                                />
                                                <Label htmlFor="active" className="form-label m-0">
                                                    {i18n.t('admin.activeUser')}
                                                </Label>
                                                {description.activeUser && (
                                                    <span className="ps-1">
                                                        <span id={`agentPermissionsTooltipactiveUser`}>
                                                            <i className="mdi mdi-help-circle-outline"></i>{' '}
                                                        </span>
                                                        <UncontrolledTooltip placement="right" target={`agentPermissionsTooltipactiveUser`}>
                                                            {description.activeUser.description}
                                                        </UncontrolledTooltip>
                                                    </span>
                                                )}
                                            </div>
                                        </Col>
                                        <Col lg={12}></Col>
                                        <Col lg={4}>
                                            <div className="mb-3">
                                                <Label htmlFor="defaultCountry" className="form-label">
                                                    {i18n.t('app.common.country')}
                                                </Label>
                                                {description.defaultCountry && (
                                                    <span className="ps-1">
                                                        <span id={`agentPermissionsTooltipdefaultCountry`}>
                                                            <i className="mdi mdi-help-circle-outline"></i>{' '}
                                                        </span>
                                                        <UncontrolledTooltip placement="right" target={`agentPermissionsTooltipdefaultCountry`}>
                                                            {description.defaultCountry.description}
                                                        </UncontrolledTooltip>
                                                    </span>
                                                )}
                                                <Select
                                                    inputId="defaultCountry"
                                                    id={formik.values?.defaultCountry}
                                                    onChange={(e) => handleDropDownChange(e, 'defaultCountry')}
                                                    options={locationValues?.country}
                                                    isDisabled={locationValues?.country?.length === 0}
                                                    placeholder={i18n.t('admin.selectCountry')}
                                                    isClearable={true}
                                                    value={
                                                        locationValues?.country?.find(
                                                            (country) => +country?.value === formik.values?.defaultCountry?.id
                                                        ) || ''
                                                    }
                                                />
                                            </div>
                                        </Col>
                                        <Col lg={4}>
                                            <div className="mb-3">
                                                <Label htmlFor="defaultCounty" className="form-label">
                                                    {i18n.t('admin.label.county')}
                                                </Label>
                                                {description.defaultCounty && (
                                                    <span className="ps-1">
                                                        <span id={`agentPermissionsTooltipdefaultCounty`}>
                                                            <i className="mdi mdi-help-circle-outline"></i>{' '}
                                                        </span>
                                                        <UncontrolledTooltip placement="right" target={`agentPermissionsTooltipdefaultCounty`}>
                                                            {description.defaultCounty.description}
                                                        </UncontrolledTooltip>
                                                    </span>
                                                )}
                                                <Select
                                                    inputId="defaultCounty"
                                                    id={formik.values?.defaultCounty}
                                                    onChange={(e) => handleDropDownChange(e, 'defaultCounty')}
                                                    options={locationValues?.county}
                                                    isDisabled={!locationValues?.county || locationValues?.county?.length === 0}
                                                    placeholder={i18n.t('admin.selectCounty')}
                                                    isClearable={true}
                                                    value={
                                                        locationValues?.county?.find(
                                                            (county) => +county?.value === formik.values?.defaultCounty?.id
                                                        ) || ''
                                                    }
                                                />
                                            </div>
                                        </Col>
                                        <Col lg={4}>
                                            <div className="mb-3">
                                                <Label htmlFor="defaultCity" className="form-label">
                                                    {i18n.t('app.common.city')}
                                                </Label>
                                                {description.defaultCity && (
                                                    <span className="ps-1">
                                                        <span id={`agentPermissionsTooltipdefaultCity`}>
                                                            <i className="mdi mdi-help-circle-outline"></i>{' '}
                                                        </span>
                                                        <UncontrolledTooltip placement="right" target={`agentPermissionsTooltipdefaultCity`}>
                                                            {description.defaultCity.description}
                                                        </UncontrolledTooltip>
                                                    </span>
                                                )}
                                                <Select
                                                    inputId="defaultCity"
                                                    id={formik.values?.defaultCity}
                                                    onChange={(e) => handleDropDownChange(e, 'defaultCity')}
                                                    options={locationValues?.city}
                                                    isDisabled={!locationValues?.city || locationValues?.city?.length === 0}
                                                    placeholder={i18n.t('admin.selectCity')}
                                                    isClearable={true}
                                                    value={
                                                        locationValues?.city?.find((city) => +city?.value === formik.values?.defaultCity?.id) || ''
                                                    }
                                                />
                                            </div>
                                        </Col>
                                        <Col lg={6}>
                                            <div className="mb-3">
                                                <Label htmlFor="applicationLanguage" className="form-label">
                                                    {i18n.t('admin.selectLanguageLabel')}
                                                </Label>
                                                {description.applicationLanguage && (
                                                    <span className="ps-1">
                                                        <span id={`agentPermissionsTooltipapplicationLanguage`}>
                                                            <i className="mdi mdi-help-circle-outline"></i>{' '}
                                                        </span>
                                                        <UncontrolledTooltip placement="right" target={`agentPermissionsTooltipapplicationLanguage`}>
                                                            {description.applicationLanguage.description}
                                                        </UncontrolledTooltip>
                                                    </span>
                                                )}
                                                <Select
                                                    inputId="applicationLanguage"
                                                    id={formik.values?.applicationLanguage}
                                                    onChange={(e) => handleDropDownChange(e, 'applicationLanguage')}
                                                    options={createSelectOptionData(availableLanguages, 'name', 'id')}
                                                    placeholder={i18n.t('admin.selectLanguage')}
                                                    value={
                                                        createSelectOptionData(availableLanguages, 'name', 'id')?.filter(
                                                            (lang) => lang?.value === formik?.values?.applicationLanguage?.id
                                                        )[0]
                                                    }
                                                />
                                            </div>
                                        </Col>
                                        <Col lg={6}>
                                            <div className="mb-3">
                                                <Label htmlFor="agentHomepage" className="form-label">
                                                    {i18n.t('admin.homepageLabel')}
                                                </Label>
                                                {description.agentHomepage && (
                                                    <span className="ps-1">
                                                        <span id={`agentPermissionsTooltipagentHomepage`}>
                                                            <i className="mdi mdi-help-circle-outline"></i>{' '}
                                                        </span>
                                                        <UncontrolledTooltip placement="right" target={`agentPermissionsTooltipagentHomepage`}>
                                                            {description.agentHomepage.description}
                                                        </UncontrolledTooltip>
                                                    </span>
                                                )}
                                                <Select
                                                    inputId="agentHomepage"
                                                    id={formik.values?.agentHomepage}
                                                    onChange={(e) => {
                                                        formik?.setFieldValue('agentHomepage', `/${e.value}`)
                                                    }}
                                                    options={createSelectOptionData(applicationPackage?.applicationModules, 'name', 'token')}
                                                    placeholder={i18n.t('admin.selectHomePage')}
                                                    value={createSelectOptionData(applicationPackage?.applicationModules, 'name', 'token')?.find(
                                                        (item) => item.value === formik.values?.agentHomepage?.substring(1)
                                                    )}
                                                />
                                            </div>
                                        </Col>
                                        <Col lg={6}>
                                            <div className="mb-3 d-flex align-items-center">
                                                <Input
                                                    type="checkbox"
                                                    checked={formik.values?.nameSync || false}
                                                    id={'nameSync'}
                                                    onChange={formik.handleChange}
                                                    className="form-control me-2 p-2"
                                                />
                                                <Label htmlFor="nameSync" className="form-label m-0">
                                                    {i18n.t('admin.syncName')}
                                                </Label>
                                                {description.nameSync && (
                                                    <span className="ps-1">
                                                        <span id={`agentPermissionsTooltipsyncName`}>
                                                            <i className="mdi mdi-help-circle-outline"></i>{' '}
                                                        </span>
                                                        <UncontrolledTooltip placement="right" target={`agentPermissionsTooltipsyncName`}>
                                                            {description.nameSync.description}
                                                        </UncontrolledTooltip>
                                                    </span>
                                                )}
                                            </div>
                                        </Col>
                                        <Col lg={6}>
                                            <div className="mb-3 d-flex align-items-center">
                                                <Input
                                                    id={'phoneNumberSync'}
                                                    type="checkbox"
                                                    checked={formik.values?.phoneNumberSync || false}
                                                    onChange={formik.handleChange}
                                                    className="form-control me-2 p-2"
                                                />
                                                <Label htmlFor="phoneNumberSync" className="form-label m-0">
                                                    {i18n.t('admin.snycPhone')}
                                                </Label>
                                                {description.phoneNumberSync && (
                                                    <span className="ps-1">
                                                        <span id={`agentPermissionsTooltipphoneNumberSync`}>
                                                            <i className="mdi mdi-help-circle-outline"></i>{' '}
                                                        </span>
                                                        <UncontrolledTooltip placement="right" target={`agentPermissionsTooltipphoneNumberSync`}>
                                                            {description.phoneNumberSync.description}
                                                        </UncontrolledTooltip>
                                                    </span>
                                                )}
                                            </div>
                                        </Col>
                                        <Col lg={6}>
                                            <div className="mb-3 d-flex align-items-center">
                                                <Input
                                                    type="checkbox"
                                                    checked={formik.values?.mobileNumberSync || false}
                                                    id={'mobileNumberSync'}
                                                    onChange={formik.handleChange}
                                                    className="form-control me-2 p-2"
                                                />
                                                <Label htmlFor="mobileNumberSync" className="form-label m-0">
                                                    {i18n.t('admin.syncMobilePhone')}
                                                </Label>
                                                {description.mobileNumberSync && (
                                                    <span className="ps-1">
                                                        <span id={`agentPermissionsTooltipmobileNumberSync`}>
                                                            <i className="mdi mdi-help-circle-outline"></i>{' '}
                                                        </span>
                                                        <UncontrolledTooltip placement="right" target={`agentPermissionsTooltipmobileNumberSync`}>
                                                            {description.mobileNumberSync.description}
                                                        </UncontrolledTooltip>
                                                    </span>
                                                )}
                                            </div>
                                        </Col>
                                        <Col lg={6}>
                                            <div className="mb-3 d-flex align-items-center">
                                                <Input
                                                    type="checkbox"
                                                    checked={formik.values?.primaryEmailSync || false}
                                                    id={'primaryEmailSync'}
                                                    onChange={formik.handleChange}
                                                    className="form-control me-2 p-2"
                                                />
                                                <Label htmlFor="primaryEmailSync" className="form-label m-0">
                                                    {i18n.t('admin.syncEmail')}
                                                </Label>
                                                {description.primaryEmailSync && (
                                                    <span className="ps-1">
                                                        <span id={`agentPermissionsTooltipprimaryEmailSync`}>
                                                            <i className="mdi mdi-help-circle-outline"></i>{' '}
                                                        </span>
                                                        <UncontrolledTooltip placement="right" target={`agentPermissionsTooltipprimaryEmailSync`}>
                                                            {description.primaryEmailSync.description}
                                                        </UncontrolledTooltip>
                                                    </span>
                                                )}
                                            </div>
                                        </Col>
                                        <Col lg={6}>
                                            <div className="mb-3 d-flex align-items-center">
                                                <Input
                                                    type="checkbox"
                                                    checked={formik.values?.photoSync || false}
                                                    id={'photoSync'}
                                                    onChange={formik.handleChange}
                                                    className="form-control me-2 p-2"
                                                />
                                                <Label htmlFor="photoSync" className="form-label m-0">
                                                    {i18n.t('admin.syncPhoto')}
                                                </Label>
                                                {description.photoSync && (
                                                    <span className="ps-1">
                                                        <span id={`agentPermissionsTooltipphotoSync`}>
                                                            <i className="mdi mdi-help-circle-outline"></i>{' '}
                                                        </span>
                                                        <UncontrolledTooltip placement="right" target={`agentPermissionsTooltipphotoSync`}>
                                                            {description.photoSync.description}
                                                        </UncontrolledTooltip>
                                                    </span>
                                                )}
                                            </div>
                                        </Col>
                                        <Col lg={6}>
                                            <div className="mb-3 d-flex align-items-center">
                                                <Input
                                                    type="checkbox"
                                                    checked={formik.values?.agencySync || false}
                                                    id={'agencySync'}
                                                    onChange={formik.handleChange}
                                                    className="form-control me-2 p-2"
                                                />
                                                <Label htmlFor="agencySync" className="form-label m-0">
                                                    {i18n.t('admin.syncAgency')}
                                                </Label>
                                                {description.agencySync && (
                                                    <span className="ps-1">
                                                        <span id={`agentPermissionsTooltipagencySync`}>
                                                            <i className="mdi mdi-help-circle-outline"></i>{' '}
                                                        </span>
                                                        <UncontrolledTooltip placement="right" target={`agentPermissionsTooltipagencySync`}>
                                                            {description.agencySync.description}
                                                        </UncontrolledTooltip>
                                                    </span>
                                                )}
                                            </div>
                                        </Col>
                                        <Col lg={6}>
                                            <div className="mb-3 d-flex align-items-center">
                                                <Input
                                                    type="checkbox"
                                                    checked={formik.values?.agencyOfficeSync || false}
                                                    id={'agencyOfficeSync'}
                                                    onChange={formik.handleChange}
                                                    className="form-control me-2 p-2"
                                                />
                                                <Label htmlFor="agencyOfficeSync" className="form-label m-0">
                                                    {i18n.t('admin.syncOffice')}
                                                </Label>
                                                {description.agencyOfficeSync && (
                                                    <span className="ps-1">
                                                        <span id={`agentPermissionsTooltipagencyOfficeSync`}>
                                                            <i className="mdi mdi-help-circle-outline"></i>{' '}
                                                        </span>
                                                        <UncontrolledTooltip placement="right" target={`agentPermissionsTooltipagencyOfficeSync`}>
                                                            {description.agencyOfficeSync.description}
                                                        </UncontrolledTooltip>
                                                    </span>
                                                )}
                                            </div>
                                        </Col>
                                    </Row>
                                </TabPane>

                                <TabPane tabId="permissions">
                                    <AgentPermissions formik={formik} />
                                </TabPane>
                                {featureFlags?.notifications_system && (
                                    <TabPane tabId="notifications">
                                        <NotificationPreferences />
                                    </TabPane>
                                )}
                            </TabContent>
                            {activeTab !== 'notifications' && (
                                <Col lg={12}>
                                    <div className="hstack gap-2 justify-content-end">
                                        <FormActionButtons disabled={false} hasCancel={true} cancelAction={() => history.push('/admin/user')} />
                                    </div>
                                </Col>
                            )}
                        </Form>
                    </CardBody>
                </Card>
            </Col>
        </Row>
    )
}

export default UserEdit
